import React, { useEffect, useMemo } from "react";
import { Icon, Container } from "semantic-ui-react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSubscriptionStatus } from "../../../../hooks/useSubscriptionStatus";

import CarryForward from "./carryForward/CarryForward";
import LeaveType from "./leaveTypes/LeaveType";
import PublicHolidays from "./publicHolidays/PublicHolidays";
import Backup from "./backup/Backup";
import PrivacyVisibility from "./general/PrivacyVisibility";
import AutoLeaveDeductionSettings from "./autoLeaveDeduction/AutoLeaveDeductionSettings";

import "./Settings.css";

const TAB_IDS = ["leavetypes", "carryforward", "publicHolidays", "backup", "privacy", "autoDeduction"];

const parseTabFromLocation = (search) => {
  const params = new URLSearchParams(search);
  const tabFromUrl = params.get("tab");
  if (tabFromUrl && TAB_IDS.includes(tabFromUrl)) {
    return tabFromUrl;
  }
  const savedTab = localStorage.getItem("leaveSettingsActiveTab");
  if (savedTab && TAB_IDS.includes(savedTab)) {
    return savedTab;
  }
  return "leavetypes";
};

const LeaveSettings = () => {
  const { t } = useTranslation();
  const { isSuspended } = useSubscriptionStatus();
  const history = useHistory();
  const location = useLocation();

  const activeTab = useMemo(() => parseTabFromLocation(location.search), [location.search]);

  useEffect(() => {
    localStorage.setItem("leaveSettingsActiveTab", activeTab);
  }, [activeTab]);

  useEffect(() => {
    const fixViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
      void document.body.offsetHeight;
      window.scrollTo(0, 0);
    };
    const timeoutId = setTimeout(() => {
      fixViewportHeight();
    }, 50);
    const handleResize = () => {
      setTimeout(fixViewportHeight, 100);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (isSuspended) {
      history.replace("/settings/company?tab=billing");
    }
  }, [isSuspended, history]);

  const renderContent = () => {
    switch (activeTab) {
      case "leavetypes":
        return <LeaveType />;
      case "carryforward":
        return <CarryForward />;
      case "publicHolidays":
        return <PublicHolidays />;
      case "backup":
        return <Backup />;
      case "privacy":
        return <PrivacyVisibility />;
      case "autoDeduction":
        return <AutoLeaveDeductionSettings />;
      default:
        return <LeaveType />;
    }
  };

  if (isSuspended) {
    return null;
  }

  return (
    <Container className="py-4 px-3 px-md-4" fluid>
      <div className="settings-header">
        <div className="settings-header-content">
          <div className="settings-title-section">
            <div className="settings-icon-wrapper">
              <Icon name="calendar alternate outline" size="large" style={{ color: "#ffffff" }} />
            </div>
            <div>
              <h1 className="settings-main-title">{t("app.settingsPage.leaveSettings")}</h1>
              <p className="settings-subtitle">{t("app.settingsPage.leaveSettingsSubtitle")}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-main-content settings-main-content--no-nav">
        <div className="settings-content">
          <div className="settings-content-card">{renderContent()}</div>
        </div>
      </div>
    </Container>
  );
};

export default LeaveSettings;
